import type { AvatarRole, ProethicsScenarioId } from "../types";
import { PROETHICS_SCENARIO_IDS } from "../types";

export type ScenarioRegistryEntry = {
	avatarRole: AvatarRole;
	personaName: string;
	// Bundled avatar asset; scenarios without one fall back to initials.
	avatarImage?: number;
	conversationGoals: string[];
	openingHint: string;
};

export const DEFAULT_SCENARIO_ID: ProethicsScenarioId = "scenario-1";

export const isProethicsScenarioId = (
	value: string | undefined,
): value is ProethicsScenarioId =>
	(PROETHICS_SCENARIO_IDS as readonly string[]).includes(value ?? "");

// Mirrors the server's scenario registry; holds what the session JSON does
// not carry (briefing copy, persona, and rubric come from the JSON).
export const SCENARIO_REGISTRY: Record<
	ProethicsScenarioId,
	ScenarioRegistryEntry
> = {
	"scenario-1": {
		avatarRole: "daria",
		personaName: "Ms. Daria",
		avatarImage: require("../../../../assets/praxis-proethica/avatars/ms-daria-avatar.png"),
		conversationGoals: [
			"Understand what she did and why she thought it was fine",
			"Help her see how sharing private information puts students at risk",
			"Agree on a safer way to handle grading going forward",
		],
		openingHint:
			"Begin by asking Ms. Daria what student information she shared with ChatGPT and why she believed that was safe.",
	},
	// Placeholder goals until the scenario 2 and 3 content drop.
	"scenario-2": {
		avatarRole: "dennis",
		personaName: "Mr. Dennis",
		avatarImage: require("../../../../assets/praxis-proethica/avatars/mr-dennis-avatar.png"),
		conversationGoals: [
			"Understand how he is using AI to produce homework materials",
			"Help him see the risks of shipping unchecked AI-generated work",
			"Agree on a review process that keeps a human in the loop",
		],
		openingHint:
			"Begin by asking Mr. Dennis how the AI produced the homework and what he did to verify its reasoning and suitability.",
	},
	"scenario-3": {
		avatarRole: "smith",
		personaName: "Mr. Smith",
		avatarImage: require("../../../../assets/praxis-proethica/avatars/mr-smith-avatar.png"),
		conversationGoals: [
			"Understand his concerns about the deepfake video of his child",
			"Acknowledge the harm while staying objective and professional",
			"Agree on next steps that follow school policy and protect the student",
		],
		openingHint:
			"Begin by asking Mr. Smith what he understands about the AI-generated video, its circulation, and its effect on the targeted student.",
	},
};

export const getPersonaInitials = (personaName: string) =>
	personaName
		.replace(/^(Ms\.|Mr\.|Mrs\.|Dr\.)\s+/i, "")
		.split(/\s+/)
		.map((part) => part.charAt(0).toUpperCase())
		.slice(0, 2)
		.join("");
